import styled, { css } from 'styled-components';
import { remCalc, layout, media } from '@prisjakt/ui-base';
import { AdPlacementContainer as AppnexusAdPlacement } from '../../ads/services/appnexus/styles';
import { TakeoverSettings } from '.';
import {
  PAGE_CONTENT_GUTTER_BREAKPOINT,
  OUTSIDE_AD_MIN_WIDTH,
  OUTSIDE_AD_MAX_WIDTH,
  SIDEBAR_CONTENT_WIDTH,
  SIDEBAR_MENU_WIDTH,
} from '../constants';
import { height as headerHeight } from 'src/areas/header/styles';
import AdContainer from '../../ads';
import { NAV_HEIGHT } from 'src/components/sticky-tab-nav/constants';

type ContentProps = {
  takeover?: TakeoverSettings;
  wideContent?: boolean;
  hasAds?: boolean;
  alignLeft?: boolean;
  isWide?: boolean;
};

export const Content = styled.div<ContentProps>(
  props => css`
    width: 100%;
    max-width: ${remCalc(props.isWide ? 1300 : 1000)};
    padding-left: 0;
    padding-right: 0;
    margin-right: ${props.alignLeft ? 'auto' : 0};

    ${props.hasAds &&
      css`
        @media ${`screen and (max-width: ${PAGE_CONTENT_GUTTER_BREAKPOINT})`} {
          /* Center content when the outsider ad is hidden */
          margin-left: ${props.takeover ? 0 : 'auto'};
          margin-right: auto;
        }
      `}

    ${props.wideContent &&
      css`
        max-width: none;
      `}

    ${props.takeover &&
      css`
        min-height: ${props.takeover.height}px;
        background-color: ${props.theme.bg.primary};
        position: relative;
        /* @NOTE: Hardcoded breakpoint based on panorama_top visible width limit */
        @media ${'screen and (min-width: 862px)'} {
          margin-top: ${`${props.takeover.topMargin +
            layout.pageContent.gutter.value}px`};
        }
      `}
  `
);

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  align-items: center;
  ${media.largeUp`
      min-width: ${layout.siteWidth.rem};
    `};
`;

type TopAdsProps = {
  takeover?: TakeoverSettings;
};
export const TopAds = styled.aside<TopAdsProps>`
  min-width: calc(100% - 2rem);
  ${props =>
    props.takeover &&
    css`
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;

      ${props.takeover.offgrid &&
        css`
          & > div {
            overflow: hidden;
            max-width: ${props.takeover.width}px;
            margin-left: -200px;
            width: calc(100vw + 200px);
          }
        `}
    `};
`;

type OutsiderAdsProps = {
  takeover?: TakeoverSettings;
};

export const OutsiderAds = styled.aside<OutsiderAdsProps>`
  position: relative;
  align-self: stretch;
  margin-bottom: ${layout.pageContent.gutter.rem};
  min-width: ${OUTSIDE_AD_MIN_WIDTH};
  max-width: ${OUTSIDE_AD_MAX_WIDTH};
  width: 100%;
  z-index: 100;

  & > * {
    margin-top: ${layout.pageContent.gutter.rem};
  }

  ${AppnexusAdPlacement} {
    margin-left: 0;
    margin-right: 0;
  }

  ${props =>
    props.takeover &&
    css`
      margin-top: ${props.takeover.height}px;
    `}
`;

export const SkyscraperAd = styled(AdContainer)<{ sticky?: boolean }>`
  & .pj-ui-ad-container {
    margin-left: 0;
    margin-right: 0;
  }

  ${p =>
    p.sticky &&
    css`
      position: sticky;
      top: calc(
        ${headerHeight.desktop} + ${layout.pageContent.gutter.rem} +
          ${remCalc(NAV_HEIGHT)}
      );
    `}
`;

type PageContentProps = {
  leftSidebarExperiment?: boolean;
  isWide?: boolean;
};
export const PageContent = styled.div<PageContentProps>(props => {
  const maxWidth = props.isWide ? 1600 : layout.pageContent.maxWidth.value;
  const pageWithSidebarContent =
    maxWidth +
    layout.pageContent.gutter.value +
    (SIDEBAR_CONTENT_WIDTH + SIDEBAR_MENU_WIDTH) * 2;

  return css`
    width: 100%;
    max-width: ${maxWidth}px;
    position: relative;
    margin-right: auto;
    margin-left: 0;
    margin-bottom: ${layout.measurements.spacing150};

    /* Align page content with header content when it exceeds the open sidebar */
    @media ${`(min-width: ${pageWithSidebarContent}px)`} {
      margin-left: calc(
        ((100vw - ${remCalc(maxWidth)}) / 2) -
          ${SIDEBAR_CONTENT_WIDTH +
            SIDEBAR_MENU_WIDTH +
            layout.pageContent.gutter.value}px
      );
    }

    ${media.mediumDown`
    margin-left: auto;
  `};

    @media ${`screen and (min-width: ${PAGE_CONTENT_GUTTER_BREAKPOINT})`} {
      display: flex;
      align-items: flex-start;
      justify-content: center;
    }

    @media ${`screen and (max-width: ${PAGE_CONTENT_GUTTER_BREAKPOINT})`} {
      ${OutsiderAds} {
        display: none;
      }
    }
  `;
});
